// Chat filter, in two parts.
//
// Slurs and hate speech: shown as asterisks to everyone, done on the server, in
// Region, World, Guild and Trade chat, private messages and guild posters. A
// trainer name, guild name or tag with one is refused instead.
//
// Swearing: allowed. Each player's Chat filter setting (on unless switched off)
// shows it as asterisks on their own screen only (maskSwears, in the client).
//
// Both cover English, Portuguese and Spanish, since players come from several countries.
//
// Text is read after lower-casing, dropping accents, swapping look-alike
// letters and number swaps back (n1gg3r, nіgger with a Cyrillic і), and with
// letters spelled out one by one joined up ("n i g g e r", "n.i.g.g.e.r").
// Repeated letters are allowed ("niiiigger"), but a doubled letter in a stem
// must stay doubled, so "Niger" the country is fine.
//
// The words below are listed here only so they can be blocked.

/** Blocked wherever the letters appear inside a word. Only stems that sit inside no ordinary word. */
const ANYWHERE: readonly string[] = [
  // English
  'nigger', 'faggot', 'tranny', 'trannie', 'kike', 'kyke', 'wetback', 'chingchong',
  'raghead', 'towelhead', 'jigaboo', 'porchmonkey', 'zipperhead', 'shemale',
  // Portuguese
  'traveco', 'boiola', 'baitola', 'viadinho',
  // Spanish
  'maricon', 'negrata',
];

/** Blocked only as a whole word (plurals too), because the letters sit inside ordinary words: coon in raccoon, spic in spicy, nigga in niggard. */
const WORDS: readonly string[] = [
  // English
  'nigga', 'niglet', 'beaner', 'fag', 'dyke', 'spic', 'chink', 'gook', 'coon', 'paki', 'kaffir', 'retard', 'retarded', 'golliwog',
  // Portuguese
  'viado', 'bicha', 'sapatao', 'crioulo', 'retardado', 'retardada',
  // Spanish
  'marica', 'sudaca', 'joto',
];

/** Phrases, matched on the cleaned words with single spaces between them. */
const PHRASES: readonly RegExp[] = [
  /\b(heil|sieg) (hitler|heil)\b/,
  /\bhitler (was right|did nothing wrong)\b/,
  /\bwhite (power|pride world wide)\b/,
  /\bgas (the|all) (jews?|gays?|blacks?)\b/,
  /\b(kill|hang|lynch|gas|exterminate) (all|the) (jews?|blacks?|gays?|muslims?|trans|mexicans?|asians?|arabs?|immigrants?|negros?|pretos?|judeus?|gays?)\b/,
  /\bjews will not replace us\b/,
  /\b(holocaust|holocausto) (never happened|didnt happen|nunca aconteceu)\b/,
  /\bmorte aos (judeus|gays|negros|pretos)\b/,
  /\bmuerte a los (judios|gays|negros)\b/,
];

/** Neo-Nazi number codes, read before number swaps are undone. */
const CODES = /(^|\D)14\W*88(\D|$)/;

const SWAP: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '6': 'g', '7': 't', '9': 'g', '@': 'a', '$': 's',
  // Cyrillic and Greek look-alikes
  'а': 'a', 'е': 'e', 'о': 'o', 'р': 'p', 'с': 'c', 'у': 'y', 'х': 'x', 'і': 'i', 'ј': 'j', 'к': 'k', 'ο': 'o', 'α': 'a', 'ι': 'i', 'ı': 'i',
};

/** A stem as a pattern: each letter may repeat, and a doubled letter must stay at least doubled. */
function stemPattern(stem: string): string {
  return (stem.match(/(.)\1*/g) ?? []).map((run) => `${run[0]}{${run.length},}`).join('');
}

/** Ordinary words that hold a stem: "snigger", "Scunthorpe". */
const NOT_AFTER: Record<string, string> = { nigger: 's', cunt: 's' };

/** One pattern for stems found anywhere in a word, one for whole words (plurals too). */
function matchers(anywhere: readonly string[], words: readonly string[]) {
  return {
    any: new RegExp(anywhere.map((w) => (NOT_AFTER[w] ? `(?<!${NOT_AFTER[w]})` : '') + stemPattern(w)).join('|')),
    word: new RegExp(`^(?:${words.map(stemPattern).join('|')})(?:s|z)?$`),
  };
}

const HATE = matchers(ANYWHERE, WORDS);

/** Lower case, accents dropped, look-alikes and number swaps undone; '!' and '|' read as an i or as a gap. */
function folds(s: string): string[] {
  const base = s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/./gu, (ch) => SWAP[ch] ?? ch);
  return [base.replace(/[!|]/g, 'i'), base.replace(/[!|]/g, ' ')];
}

/** Words as written, plus runs of one- and two-letter pieces joined up ("n i g g e r" reads as one word). */
function wordsOf(folded: string): string[] {
  const words = folded.split(/[^a-z]+/).filter(Boolean);
  const out = [...words];
  let run: string[] = [];
  const flush = () => { if (run.length >= 3) out.push(run.join('')); run = []; };
  for (const w of words) { if (w.length <= 2) run.push(w); else flush(); }
  flush();
  return out;
}

/** Whether a chat line, message or name carries a slur or hate speech. */
export function isHateful(text: string): boolean {
  const raw = String(text ?? '');
  if (!raw) return false;
  if (CODES.test(raw)) return true;
  return folds(raw).some((f) => {
    if (wordsOf(f).some((w) => HATE.any.test(w) || HATE.word.test(w))) return true;
    // Any gap counts as a space, so names joined with underscores read too ("kill_all_jews").
    const line = f.split(/[^a-z]+/).filter(Boolean).join(' ');
    return PHRASES.some((p) => p.test(line));
  });
}

// Swear words for the per-player Chat filter setting. Never blocked, only hidden for those who keep the filter on.
const SWEAR_ANYWHERE: readonly string[] = [
  'fuck', 'shit', 'cunt', 'bitch', 'asshole', 'whore', 'slut', 'bastard', 'twat', 'wank', 'dickhead', 'bollock',
  'caralho', 'porra', 'merda', 'buceta', 'arrombad', 'mierda', 'joder', 'pendej', 'cabron', 'gilipolla', 'chingad',
];
const SWEAR_WORDS: readonly string[] = [
  'ass', 'arse', 'dick', 'cock', 'pussy', 'piss', 'pissed', 'prick', 'damn', 'crap', 'wtf', 'stfu', 'mf',
  'puta', 'puto', 'cu', 'fdp', 'foda', 'fode', 'foder', 'fodase', 'pqp', 'vsf', 'tnc', 'cona', 'culo', 'verga', 'chinga',
];
const SWEAR = matchers(SWEAR_ANYWHERE, SWEAR_WORDS);

function isSwear(text: string): boolean {
  return folds(text).some((f) => wordsOf(f).some((w) => SWEAR.any.test(w) || SWEAR.word.test(w)));
}

/**
 * The text with every flagged piece turned to asterisks, spaces kept. A piece is the smallest run of
 * up to 8 words that the test flags, so "n i g g e r" and "heil hitler" are hidden whole and the rest stays.
 */
function maskWith(text: string, test: (s: string) => boolean): string {
  const toks = [...text.matchAll(/\S+/g)].map((m) => ({ at: m.index!, s: m[0], hid: false }));
  const stars = (s: string) => '*'.repeat([...s].length);
  for (let n = 1; n <= Math.min(8, toks.length); n++) {
    for (let i = 0; i + n <= toks.length; i++) {
      const win = toks.slice(i, i + n);
      if (win.some((t) => t.hid)) continue;
      if (test(win.map((t) => t.s).join(' '))) for (const t of win) t.hid = true;
    }
  }
  if (!toks.some((t) => t.hid)) return text;
  let out = '', at = 0;
  for (const t of toks) { out += text.slice(at, t.at) + (t.hid ? stars(t.s) : t.s); at = t.at + t.s.length; }
  return out + text.slice(at);
}

/** Slurs and hate speech as asterisks: run on the server for everything other players read. */
export function maskHate(text: string): string {
  if (!isHateful(text)) return text;
  const out = maskWith(text, isHateful);
  // Spread too wide to pin down: hide the whole line.
  return isHateful(out) ? text.replace(/\S/gu, '*') : out;
}

/** Swearing as asterisks: the client runs it when the viewer's Chat filter setting is on. */
export function maskSwears(text: string): string {
  return isSwear(text) ? maskWith(text, isSwear) : text;
}

/** What a player sees when a name is stopped. */
export const HATE_NAME = 'That name is not allowed. Try another.';
